// CreditNova Document Upload Zone Component
// Reusable categorized drag-and-drop zone for the unified upload page.
// Each zone handles one document type and accepts multiple files.
//
// Used by: apps/web/src/routes/workflow/_layout/upload.tsx
//
// Props:
//   documentType  — which type this zone accepts (determines icon, label, colors)
//   files         — current file list (controlled by parent)
//   onFilesChange — callback when files are added or removed
//   requirePeriod — if true, each file must have a period label before submission
//   maxFiles      — cap on how many files this zone accepts (default: 12)
//   disabled      — grays out the zone (used during processing)

import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Upload,
  FileText,
  FileBarChart,
  CreditCard,
  Banknote,
  Building2,
  X,
} from 'lucide-react';

// ===========================================
// Types
// ===========================================

export type DocumentType = 'financial_statement' | 'bank_statement' | 'credit_report' | 'corporate_search';

export interface UploadedFile {
  file: File;
  id: string;          // Unique client-side ID for React keys and removal
  period: string;      // User-provided period label (e.g., "Jan 2025", "FYE 2024")
  label: string;       // Display label (defaults to filename)
}

interface DocumentUploadZoneProps {
  documentType: DocumentType;
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  requirePeriod?: boolean;
  maxFiles?: number;
  disabled?: boolean;
}

// ===========================================
// Document Type Metadata
// Maps each document type to its display properties (icon, label, description).
// ===========================================

const DOCUMENT_TYPE_META: Record<DocumentType, {
  icon: typeof FileText;
  label: string;
  description: string;
  color: string;        // Tailwind text color class for the icon
  bgColor: string;      // Tailwind background class for the drop zone highlight
}> = {
  financial_statement: {
    icon: FileBarChart,
    label: 'Financial Statements',
    description: 'Income statements, balance sheets, cash flow statements',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
  },
  bank_statement: {
    icon: Banknote,
    label: 'Bank Statements',
    description: 'Monthly or quarterly bank account statements',
    color: 'text-green-500',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
  },
  credit_report: {
    icon: CreditCard,
    label: 'Credit Reports',
    description: 'Equifax, TransUnion, Experian, or other bureau reports',
    color: 'text-purple-500',
    bgColor: 'bg-purple-50 dark:bg-purple-950/20',
  },
  corporate_search: {
    icon: Building2,
    label: 'Corporate Searches',
    description: 'Corporate registry, D&B, or government filings',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-950/20',
  },
};

// ===========================================
// Helpers
// ===========================================

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Attempt to infer a period label (e.g., "December 2025", "Q4 2024") from a filename.
// Returns an empty string when no recognizable date pattern is found.
function inferPeriodFromFilename(filename: string): string {
  // Strip extension and replace common separators with spaces
  const name = filename.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');

  // Month name + year: "December 2025", "Jan 2025", "bs  March 2025"
  const monthNameMatch = name.match(
    /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{4})\b/i,
  );
  if (monthNameMatch) {
    return `${monthNameMatch[1]} ${monthNameMatch[2]}`;
  }

  // Year + month name: "2025 January"
  const yearMonthNameMatch = name.match(
    /\b(\d{4})\s+(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\b/i,
  );
  if (yearMonthNameMatch) {
    return `${yearMonthNameMatch[2]} ${yearMonthNameMatch[1]}`;
  }

  // Quarter + year: "Q4 2024", "Q1 2025"
  const quarterMatch = name.match(/\b(Q[1-4])\s*(\d{4})\b/i);
  if (quarterMatch) {
    return `${quarterMatch[1].toUpperCase()} ${quarterMatch[2]}`;
  }

  // FYE / fiscal year: "FYE 2024", "FY 2024"
  const fyMatch = name.match(/\b(FYE?)\s*(\d{4})\b/i);
  if (fyMatch) {
    return `${fyMatch[1].toUpperCase()} ${fyMatch[2]}`;
  }

  // Numeric YYYY-MM or YYYY MM: "2025 01", "2025-01"
  const MONTH_NAMES = [
    '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const numericMatch = name.match(/\b(\d{4})\s*(\d{2})\b/);
  if (numericMatch) {
    const month = parseInt(numericMatch[2], 10);
    if (month >= 1 && month <= 12) {
      return `${MONTH_NAMES[month]} ${numericMatch[1]}`;
    }
  }

  // MM-YYYY or MM YYYY: "01 2025"
  const mmYYYYMatch = name.match(/\b(\d{2})\s+(\d{4})\b/);
  if (mmYYYYMatch) {
    const month = parseInt(mmYYYYMatch[1], 10);
    if (month >= 1 && month <= 12) {
      return `${MONTH_NAMES[month]} ${mmYYYYMatch[2]}`;
    }
  }

  return '';
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// ===========================================
// DocumentUploadZone Component
// ===========================================

export function DocumentUploadZone({
  documentType,
  files,
  onFilesChange,
  requirePeriod = false,
  maxFiles = 12,
  disabled = false,
}: DocumentUploadZoneProps) {
  const meta = DOCUMENT_TYPE_META[documentType];
  const Icon = meta.icon;

  // react-dropzone callback: accepts new files dropped or selected via picker.
  // Generates a unique ID and default label for each file, then appends to the
  // existing file list. Silently ignores files beyond the maxFiles cap.
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const remainingSlots = maxFiles - files.length;
    const newFiles = acceptedFiles.slice(0, remainingSlots).map((file) => ({
      file,
      id: generateId(),
      period: inferPeriodFromFilename(file.name),
      label: file.name,
    }));
    onFilesChange([...files, ...newFiles]);
  }, [files, maxFiles, onFilesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    disabled: disabled || files.length >= maxFiles,
    multiple: true,
  });

  // Remove a file by its client-side ID
  const handleRemoveFile = (fileId: string) => {
    onFilesChange(files.filter((f) => f.id !== fileId));
  };

  // Update the period label for a specific file
  const handlePeriodChange = (fileId: string, period: string) => {
    onFilesChange(
      files.map((f) => (f.id === fileId ? { ...f, period } : f))
    );
  };

  return (
    <Card className={cn(
      'border-2 rounded-2xl transition-all',
      disabled && 'opacity-60 pointer-events-none',
    )}>
      {/* Zone Header */}
      <div className="p-4 pb-2 flex items-center gap-3">
        <Icon className={cn('w-6 h-6', meta.color)} />
        <div>
          <h3 className="font-semibold text-base">{meta.label}</h3>
          <p className="text-sm text-muted-foreground">{meta.description}</p>
        </div>
        {files.length > 0 && (
          <span className="ml-auto text-sm font-medium text-muted-foreground">
            {files.length} file{files.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Drop Zone */}
      <div className="px-4 pb-4">
        <div
          {...getRootProps()}
          className={cn(
            'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all',
            isDragActive
              ? `border-primary ${meta.bgColor}`
              : 'border-muted-foreground/25 hover:border-primary/50',
            files.length >= maxFiles && 'cursor-not-allowed opacity-50',
          )}
        >
          <input {...getInputProps()} />
          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          {isDragActive ? (
            <p className="text-sm font-medium text-primary">Drop files here...</p>
          ) : (
            <>
              <p className="text-sm font-medium">
                Drag & drop PDF files here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF only, 50MB max per file
                {maxFiles < Infinity && ` (up to ${maxFiles} files)`}
              </p>
            </>
          )}
        </div>

        {/* File List — shows each uploaded file with period input and remove button */}
        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            {files.map((uploadedFile) => (
              <div
                key={uploadedFile.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-accent/30 border"
              >
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(uploadedFile.file.size)}
                  </p>
                </div>
                {/* Period input — shown for all types when requirePeriod is true,
                    or always for bank statements since period context is important */}
                {(requirePeriod || documentType === 'bank_statement') && (
                  <Input
                    placeholder="Period (e.g., Jan 2025)"
                    value={uploadedFile.period}
                    onChange={(e) => handlePeriodChange(uploadedFile.id, e.target.value)}
                    className="w-44 h-8 text-sm"
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(uploadedFile.id)}
                  className="h-8 w-8 p-0 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
